import { useState, useEffect, useCallback, useRef } from "react";
import logger from "logging/logger";
import { deleteFile } from "services/firebaseService/files";
import { getGooglePicBySize, isGoogleUserImage } from "helpers/profileHelper";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import useUserProfile from "modules/userProfile/hooks/useUserProfile";
import useAuth from "modules/auth/hooks/useAuth";
import { useI18n } from "i18n";
import { lazy, object, string } from "yup";
import { VALID_URL_REGEX } from "helpers/urlHelper";
import { IUserProfile, IUser } from "modules/app/types";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Form } from "@remo-co/ui-core/src/components/Form";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { useMediaQuery } from "@remo-co/ui-core/src/hooks/useMediaQuery";
import { useTheme } from "@remo-co/ui-core/src/hooks/useTheme";
import { IAfterFileUpload } from "../../services/uploadService/uploadService";
import { useStyles } from "./styles";
import ProfileInput from "./ProfileInput";
import ProfileImageEditor from "./ProfileImageEditor";

interface IEditProfileContainerProps {
  onSaved?: () => void;
  onClose?: () => void;
}
const EditProfileContainer = ({
  onSaved,
  onClose,
}: IEditProfileContainerProps) => {
  const [profileData, setProfileData] = useState<IUserProfile | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setAuthUser } = useAuth();
  const { t } = useI18n(["common", "eventForm", "server"]);
  const { getMyProfile, updateMyProfile } = useUserProfile();
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const defaultProfileImage =
    profileData && profileData.picture
      ? getGooglePicBySize(profileData.picture, 500)
      : "default";
  const [profileImage, setProfileImage] = useState<string>(defaultProfileImage);
  const pictureUrls = useRef<string[]>([profileImage]);
  const updatedUrl = useRef<string>();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const classes = useStyles();

  const afterImageUpload: IAfterFileUpload<unknown> = async (
    err,
    _,
    url,
    file,
    // eslint-disable-next-line max-params
  ) => {
    if (err) {
      addErrorNotification({ message: t(err.message) });

      return;
    }

    if (file) {
      if (!url) {
        throw new Error(`Url not formed for file ${file.name}`);
      }

      const userProfile = await updateMyProfile({
        ...profileData,
        picture: url,
      });

      setIsLoading(false);
      setProfileImage(getGooglePicBySize(url, 500));
      pictureUrls.current.push(url);
      updatedUrl.current = userProfile.profile.picture;
      setAuthUser(userProfile);
    }
  };

  const onDestroy = async () => {
    if (pictureUrls.current) {
      const tasks: Promise<void>[] = pictureUrls.current.map(async (url) => {
        if (url !== updatedUrl.current && url !== "default") {
          try {
            await deleteFile(url);
          } catch (e) {
            logger.error(
              `[ProfilePage][onDestroy] Not able to delete file.${e}`,
            );
          }
        }
      });

      await Promise.all(tasks);
    }
  };

  const clearImage = async () => {
    await onDestroy();
    const userProfile = await updateMyProfile({
      ...profileData,
      picture: "default",
    });

    setProfileImage("default");
    setAuthUser(userProfile);
  };

  const saveChanges = useCallback(
    async (profile: IUserProfile) => {
      if (!profile?.firstName || !profile?.lastName) {
        addErrorNotification({
          message: t("eventForm:fill.name.save.changes"),
        });

        return;
      }

      setIsLoading(true);
      const newProfileImage =
        isGoogleUserImage(profileImage) && profile
          ? profile.picture
          : profileImage || "default";

      try {
        const userProfile = await updateMyProfile({
          ...profile,
          picture: newProfileImage,
        });

        setProfileImage(
          getGooglePicBySize(userProfile.profile.picture ?? "", 500),
        );
        updatedUrl.current = userProfile.profile.picture;
        setAuthUser(userProfile);
        addSuccessNotification({
          message: t("eventForm:profile.updated.success"),
          position: "tc",
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "";
        logger.error(
          `[EditProfileContainer][saveChanges] Error saving profile: ${errorMessage}`,
          error instanceof Object ? error : undefined,
        );
        addErrorNotification({ message: t(errorMessage) });
      }

      if (onSaved) {
        onSaved();
      } else {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileImage],
  );

  useEffect(() => {
    const getUser = async () => {
      let user: IUser | undefined;

      try {
        user = await getMyProfile();

        const userProfilePicture = user.profile.picture ?? "";

        // show single name field as first name if the latter doesn't exist
        const firstName =
          user.profile.name && !user.profile.firstName
            ? user.profile.name
            : user.profile.firstName;

        setProfileData({ ...user.profile, firstName });
        setProfileImage(getGooglePicBySize(userProfilePicture, 500));

        updatedUrl.current = userProfilePicture;
        pictureUrls.current = [userProfilePicture];
      } catch (error) {
        logger.error(
          "[EditProfileContainer] An error occurred during profile image setting",
          {
            error,
            user,
          },
        );
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    getUser();

    return () => {
      onDestroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const urlValidation = lazy((value) =>
    value === ""
      ? string().nullable(true)
      : string().matches(VALID_URL_REGEX).nullable(true),
  );

  return (
    <Container className={classes.profileContainer}>
      {isLoading && <PageLoader message={t("wait")} />}
      {profileData && (
        <Form
          initialValues={profileData}
          onSubmit={saveChanges}
          validationSchema={object().shape({
            firstName: string().max(50),
            lastName: string().max(50),
            headline: string().nullable(true),
            title: string().nullable(true),
            linkedInProfileURL: urlValidation,
            facebookProfileURL: urlValidation,
            meetingLink: urlValidation,
            sansanProfileURL: urlValidation,
            website: urlValidation,
          })}
        >
          <ProfileImageEditor
            size={130}
            profileImage={profileImage}
            afterImageUpload={afterImageUpload}
            onLoading={setIsLoading}
            setProfileImage={setProfileImage}
            showTitle
            onClear={clearImage}
            isLoading={isLoading}
          />
          <Container top={30}>
            <Container
              flex
              column={{ xs: true, sm: true, md: false }}
              alignItems="flex-start"
            >
              <ProfileInput
                name="firstName"
                label={t("first.name")}
                required
                inputProps={{
                  maxLength: 50,
                  "data-testid": "firstName",
                }}
              />
              <ProfileInput
                name="lastName"
                label={t("last.name")}
                required
                inputProps={{
                  maxLength: 50,
                  "data-testid": "lastName",
                }}
              />
            </Container>
            <ProfileInput
              name="headline"
              label={t("eventForm:headline")}
              inputProps={{ "data-testid": "headline" }}
            />
            <ProfileInput
              name="company"
              label={t("company")}
              inputProps={{ "data-testid": "company" }}
            />
            <Container
              flex
              column={{ xs: true, sm: true, md: false }}
              alignItems="flex-start"
            >
              <ProfileInput
                name="title"
                label={t("job.title")}
                inputProps={{ "data-testid": "jobTitle" }}
              />
              <ProfileInput
                name="linkedInProfileURL"
                label={t("eventForm:linkedin.public.profile.url")}
                isUrl
                inputProps={{ "data-testid": "linkedInProfileURL" }}
              />
            </Container>
            <Container
              flex
              column={{ xs: true, sm: true, md: false }}
              alignItems="flex-start"
            >
              <ProfileInput
                name="facebookProfileURL"
                label={t("eventForm:facebook.profile.url")}
                isUrl
                inputProps={{ "data-testid": "facebookProfileURL" }}
              />
              <ProfileInput
                name="meetingLink"
                label={t("eventForm:meeting.schedule.link")}
                isUrl
                inputProps={{ "data-testid": "meetingLink" }}
              />
            </Container>
            <Container
              flex
              column={{ xs: true, sm: true, md: false }}
              alignItems="flex-start"
            >
              <ProfileInput
                name="sansanProfileURL"
                label={t("eventForm:sansan.profile.url")}
                isUrl
                inputProps={{ "data-testid": "sansanProfileURL" }}
              />
              <ProfileInput
                name="website"
                label={t("website")}
                isUrl
                inputProps={{ "data-testid": "website" }}
              />
            </Container>

            <Container
              size="xs"
              flex
              column={{ xs: true, sm: true, md: false }}
              justifyContent="flex-start"
            >
              <Container
                right={{ sm: null, md: "sm" }}
                fullWidth={{ xs: true, sm: true, md: false }}
                bottom={{ xs: "sm", sm: "sm", md: null }}
              >
                <Button
                  variant="primary"
                  color="blue"
                  fullWidth={isSmallScreen}
                  type="submit"
                >
                  {t("button.save.change")}
                </Button>
              </Container>
              <Button
                variant="tertiary"
                color="blue"
                fullWidth={isSmallScreen}
                onClick={onClose}
              >
                {t("button.cancel")}
              </Button>
            </Container>
          </Container>
        </Form>
      )}
    </Container>
  );
};

export default EditProfileContainer;
